import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { RepairOption, Device } from '../types'

const DeviceRepairsPage: React.FC = () => {
  const { brandSlug, deviceSlug } = useParams<{
    brandSlug: string
    deviceSlug: string
  }>()
  const navigate = useNavigate()
  const [repairs, setRepairs] = useState<RepairOption[]>([])
  const [device, setDevice] = useState<Device | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchDeviceRepairs = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
        const response = await fetch(
          `${apiUrl}/repairs/device-slug/${brandSlug}/${deviceSlug}`
        )
        const data = await response.json()

        if (!data.success) {
          setError('Gerät nicht gefunden')
          return
        }

        setRepairs(data.data)
        setDevice(data.device)
      } catch (err) {
        console.error('Error fetching device repairs:', err)
        setError('Fehler beim Laden der Reparaturen')
      } finally {
        setLoading(false)
      }
    }

    fetchDeviceRepairs()
  }, [brandSlug, deviceSlug])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Reparaturen...</p>
        </div>
      </div>
    )
  }

  if (error || !device) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-900">Gerät nicht gefunden</h2>
          <p className="text-gray-600 mb-8">{error || 'Das gesuchte Gerät existiert nicht.'}</p>
          <button
            onClick={() => navigate('/repair')}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
          >
            Zur Reparaturübersicht
          </button>
        </div>
      </div>
    )
  }

  // Breadcrumb structured data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://phone-medic.de"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": device.brand,
        "item": `https://phone-medic.de/${brandSlug}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": device.deviceModel,
        "item": `https://phone-medic.de/${brandSlug}/${deviceSlug}`
      }
    ]
  }

  const seoTitle = `${device.deviceModel} Reparatur | Alle Services | PhoneMedic`
  const seoDescription = device.seoDescription || `Professionelle ${device.deviceModel} Reparatur. Wählen Sie aus ${repairs.length} verschiedenen Reparatur-Services. ✓ Garantie ✓ Express-Service verfügbar.`

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />

        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={device.image || '/images/devices/default.png'} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://phone-medic.de/${brandSlug}/${deviceSlug}`} />

        {/* Canonical URL */}
        <link rel="canonical" href={`https://phone-medic.de/${brandSlug}/${deviceSlug}`} />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "PhoneMedic",
          "url": "https://phone-medic.de",
          "telephone": "+491605010907",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "32",
            "bestRating": "5"
          }
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8 md:py-12">
        <div className="container mx-auto px-4 max-w-7xl">

          {/* Breadcrumbs */}
          <nav className="mb-6 md:mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center flex-wrap space-x-2 text-gray-600">
              <li><Link to="/" className="hover:text-indigo-600 transition">Home</Link></li>
              <li>/</li>
              <li><Link to={`/repair?brand=${device.brand}`} className="hover:text-indigo-600 transition">Reparaturen</Link></li>
              <li>/</li>
              <li className="text-gray-900 font-medium">{device.deviceModel}</li>
            </ol>
          </nav>

          {/* Back button */}
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate(`/repair?brand=${device.brand}`)}
              className="text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
            >
              ← Zurück zur Modellauswahl
            </button>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {device.deviceModel} Reparatur
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Wählen Sie aus {repairs.length} professionellen Reparatur-Services für Ihr {device.deviceModel}
            </p>
          </div>

          {/* Device Image (optional) */}
          {device.image && (
            <div className="max-w-md mx-auto mb-12">
              <img
                src={device.image}
                alt={device.deviceModel}
                className="w-full rounded-lg shadow-lg"
              />
            </div>
          )}

          {/* Repairs Grid */}
          {repairs.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {repairs.map((repair) => (
                <Link
                  key={repair._id}
                  to={`/${brandSlug}/${deviceSlug}/${repair.slug}`}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all p-6 group"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition">
                      {repair.title}
                    </h2>
                    {repair.expressAvailable && (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded font-semibold">
                        Express
                      </span>
                    )}
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {repair.description}
                  </p>

                  <div className="space-y-2 mb-4 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <span>Dauer:</span>
                      <span className="font-semibold">{repair.duration}</span>
                    </div>
                    {repair.warranty > 0 && (
                      <div className="flex justify-between">
                        <span>Garantie:</span>
                        <span className="font-semibold">{repair.warranty} Monate</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-end justify-between">
                      <div>
                        {repair.originalPrice && repair.originalPrice > repair.price && (
                          <span className="text-gray-400 line-through text-sm block">
                            {repair.originalPrice.toFixed(2)}€
                          </span>
                        )}
                        <span className="text-3xl font-bold text-indigo-600">
                          {repair.price.toFixed(2)}€
                        </span>
                      </div>
                      <span className="text-indigo-600 font-semibold group-hover:underline">
                        Details →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                Für dieses Gerät sind aktuell keine Reparaturen verfügbar.
              </p>
              <button
                onClick={() => navigate('/repair')}
                className="mt-6 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
              >
                Andere Geräte ansehen
              </button>
            </div>
          )}

          {/* Why Choose Us Section */}
          <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Warum PhoneMedic?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2">Garantie</h3>
                <p className="text-gray-600">Bis zu 12 Monate auf alle Reparaturen</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2">Express-Service</h3>
                <p className="text-gray-600">Same-Day repair</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="font-bold text-lg mb-2">Original-Qualität</h3>
                <p className="text-gray-600">Hochwertige Ersatzteile</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  )
}

export default DeviceRepairsPage
